import classNames from 'classnames';
import React from 'react';

import {useMatchMedia} from '@udemy/hooks';
import {useI18n} from '@udemy/i18n';
import FacebookIcon from '@udemy/icons/dist/facebook.ud-icon';
import LinkedinIcon from '@udemy/icons/dist/linkedin.ud-icon';
import ShareIcon from '@udemy/icons/dist/share.ud-icon';
import TwitterIcon from '@udemy/icons/dist/twitter.ud-icon';
import {Button, IconButton} from '@udemy/react-core-components';
import {Popover, type PopoverProps} from '@udemy/react-popup-components';
import {Tooltip} from '@udemy/react-popup-components';

import styles from '../google-tracker.module.less';

export interface CertificateShareLinks {
    certificateUrl?: string;
    completionDate?: string;
    isReady?: boolean;
    facebookUrl?: string;
    twitterUrl?: string;
    linkedinUrl?: string;
}

export interface RedirectButtonsProps {
    certificate?: CertificateShareLinks;
}

export const GoogleRedirectButtons: React.FC<RedirectButtonsProps> = ({certificate}) => {
    const {gettext} = useI18n();
    const isMobile = useMatchMedia('mobile-max');

    const hasShareLinks =
        certificate?.facebookUrl || certificate?.twitterUrl || certificate?.linkedinUrl;

    const sharePopoverContent =
        certificate && hasShareLinks ? (
            <>
                <div className={`ud-heading-md ${styles['popover-title']}`}>
                    {gettext('Share this certificate')}
                </div>
                <div className={styles['popover-items']}>
                    {certificate.facebookUrl && (
                        <a target="_blank" rel="noopener noreferrer" href={certificate.facebookUrl}>
                            <FacebookIcon label="Facebook" size="large" />
                        </a>
                    )}
                    {certificate.twitterUrl && (
                        <a target="_blank" rel="noopener noreferrer" href={certificate.twitterUrl}>
                            <TwitterIcon label="Twitter" size="large" />
                        </a>
                    )}
                    {certificate.linkedinUrl && (
                        <a target="_blank" rel="noopener noreferrer" href={certificate.linkedinUrl}>
                            <LinkedinIcon label="LinkedIn" size="large" />
                        </a>
                    )}
                </div>
            </>
        ) : null;

    const renderSharePopoverContent: PopoverProps['renderContent'] = (
        {className, ...props},
        placement,
        offset,
    ) =>
        Popover.defaultProps.renderContent?.(
            {className: classNames(className, styles['share-popover']), ...props},
            placement,
            offset,
        );

    const sharePopover =
        hasShareLinks && sharePopoverContent ? (
            <Popover
                placement="top"
                renderContent={renderSharePopoverContent}
                trigger={
                    !isMobile ? (
                        <Button className={styles['redirect-button-wide']} udStyle="secondary">
                            <ShareIcon label={false} />
                            {gettext('Share')}
                        </Button>
                    ) : (
                        <IconButton
                            aria-label={gettext('Share this certificate')}
                            size="medium"
                            udStyle="secondary"
                        >
                            <ShareIcon label={false} />
                        </IconButton>
                    )
                }
            >
                {sharePopoverContent}
            </Popover>
        ) : null;
    const isViewCertificateEnabled =
        Boolean(certificate?.certificateUrl) && Boolean(certificate?.isReady);

    const viewCertificateButton = (
        <Button
            className={`${styles['redirect-button']} ${styles['redirect-button-wide']}`}
            componentClass="a"
            disabled={!isViewCertificateEnabled}
            href={isViewCertificateEnabled ? certificate?.certificateUrl ?? '' : undefined}
            rel="noopener noreferrer"
            target="_blank"
        >
            {gettext('View certificate')}
        </Button>
    );

    const certificateButton = isViewCertificateEnabled ? (
        viewCertificateButton
    ) : (
        <Tooltip placement="top" trigger={viewCertificateButton}>
            {gettext('Your certificate is being prepared')}
        </Tooltip>
    );

    return (
        <div className={styles['redirect-buttons']}>
            {certificateButton}
            {hasShareLinks && sharePopover}
        </div>
    );
};
